import React, { useState } from 'react';
import api from '../services/api';
import { useAlert } from '../context/AlertContext';
import './RSVPList.css';

const RSVPList = ({ rsvps: initialRsvps, breakdown, eventId, filter }) => {
  const { showAlert } = useAlert();
  const [rsvps, setRsvps] = useState(initialRsvps);
  const [activating, setActivating] = useState(null);

  const getFilteredRsvps = (allRsvps) => {
    if (!filter) return allRsvps;

    if (filter === 'unpaidContractors') {
      return allRsvps.filter(
        r => r.registrationType === 'contractor' &&
             (r.paymentStatus === 'pending' || r.paymentStatus === 'failed')
      );
    }

    if (filter === 'waitlistedLayton') {
      return allRsvps.filter(
        r => r.registrationType === 'employee' && r.employeeStatus === 'waitlisted'
      );
    }

    return allRsvps;
  };

  const handleActivate = async (rsvp) => {
    setActivating(rsvp.id);
    try {
      await api.post(`/events/${eventId}/rsvps/${rsvp.id}/activate`);
      setRsvps(prev => prev.map(r => r.id === rsvp.id ? { ...r, employeeStatus: 'active' } : r));
      showAlert(`${rsvp.name} has been activated and notified by email.`, 'success');
    } catch (err) {
      showAlert(err.response?.data?.detail || 'Failed to activate RSVP', 'error');
    } finally {
      setActivating(null);
    }
  };

  const filteredRsvps = getFilteredRsvps(rsvps);

  if (filteredRsvps.length === 0) {
    return (
      <div className="rsvp-empty-state">
        <div className="rsvp-empty-icon">📋</div>
        <h3 className="rsvp-empty-title">No matching RSVPs</h3>
        <p className="rsvp-empty-message">No RSVPs match the selected filter</p>
      </div>
    );
  }

  const waitlisted = filteredRsvps.filter(r => r.registrationType === 'employee' && r.employeeStatus === 'waitlisted');
  const active = filteredRsvps.filter(r => !(r.registrationType === 'employee' && r.employeeStatus === 'waitlisted'));

  const renderRsvp = (rsvp) => {
    const isWaitlisted = rsvp.registrationType === 'employee' && rsvp.employeeStatus === 'waitlisted';
    const isLowPayment = rsvp.registrationType === 'contractor' && rsvp.paymentStatus === 'paid' && (rsvp.amountPaid || 0) < 30000; // Less than $300
    
    return (
      <div key={rsvp.id} className={`rsvp-card ${isWaitlisted ? 'waitlisted' : ''} ${isLowPayment ? 'low-payment' : ''}`}>
        {/* Contact Info */}
        <div className="rsvp-contact">
          <div className="rsvp-name">{rsvp.name}</div>
          <div className="rsvp-email">{rsvp.email}</div>
          <div className="rsvp-phone">{rsvp.phone}</div>
        </div>

        {/* Company & Details */}
        <div className="rsvp-details">
          {rsvp.company && (
            <div className="rsvp-detail-row">
              <span className="rsvp-detail-label">Company:</span>
              {rsvp.company}
            </div>
          )}
          {rsvp.specialty && (
            <div className="rsvp-detail-row">
              <span className="rsvp-detail-label">Specialty:</span>
              {rsvp.specialty}
            </div>
          )}
          <div className="rsvp-detail-row">
            <span className="rsvp-detail-label">Guests:</span>
            {rsvp.guests}
          </div>
        </div>

        {/* Status & Actions */}
        <div className="rsvp-status">
          <div className="rsvp-badges">
            {/* Registration Type Badge */}
            <span className={`badge badge-${rsvp.registrationType}`}>
              {rsvp.registrationType === 'employee' ? 'Employee' : 'Contractor'}
            </span>

            {/* Payment Status Badge */}
            {rsvp.paymentStatus === 'paid' ? (
              <span className={`badge ${rsvp.amountPaid > 0 ? 'badge-paid' : 'badge-free'}`}>
                {rsvp.amountPaid > 0 ? `Paid $${(rsvp.amountPaid / 100).toFixed(2)}` : 'Free'}
              </span>
            ) : rsvp.paymentStatus === 'pending' ? (
              <span className="badge badge-pending">Payment Pending</span>
            ) : (
              <span className="badge badge-failed">Failed</span>
            )}

            {/* Waitlisted Badge */}
            {isWaitlisted && (
              <span className="badge badge-waitlisted">Waitlisted</span>
            )}
          </div>

          {/* Activate Button */}
          {isWaitlisted && (
            <button
              onClick={() => handleActivate(rsvp)}
              disabled={activating === rsvp.id}
              className="activate-button"
            >
              {activating === rsvp.id ? 'Activating...' : 'Activate'}
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="rsvp-list">
      {/* Summary Panel */}
      <div className="rsvp-summary">
        <div className="rsvp-summary-title">{filter ? 'Filtered Results' : 'Total Submissions'}</div>
        <div className="rsvp-summary-stats">
          <div className="rsvp-stat">
            <span className="rsvp-stat-label">Total:</span>
            <span className="rsvp-stat-value">{filteredRsvps.length}</span>
          </div>

          {!filter && breakdown && (
            <>
              <span className="rsvp-stat-separator">•</span>
              <div className="rsvp-stat">
                <span className="rsvp-stat-label">Confirmed (counted):</span>
                <span className="rsvp-stat-value confirmed">{breakdown.paid}</span>
              </div>

              {breakdown.pending > 0 && (
                <>
                  <span className="rsvp-stat-separator">•</span>
                  <div className="rsvp-stat">
                    <span className="rsvp-stat-label">Awaiting Payment:</span>
                    <span className="rsvp-stat-value pending">{breakdown.pending}</span>
                  </div>
                </>
              )}

              {breakdown.failed > 0 && (
                <>
                  <span className="rsvp-stat-separator">•</span>
                  <div className="rsvp-stat">
                    <span className="rsvp-stat-label">Failed:</span>
                    <span className="rsvp-stat-value failed">{breakdown.failed}</span>
                  </div>
                </>
              )}

              {waitlisted.length > 0 && (
                <>
                  <span className="rsvp-stat-separator">•</span>
                  <div className="rsvp-stat">
                    <span className="rsvp-stat-label">Waitlisted:</span>
                    <span className="rsvp-stat-value waitlisted">{waitlisted.length}</span>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* Active RSVPs */}
      {active.map(renderRsvp)}

      {/* Waitlisted Section */}
      {waitlisted.length > 0 && (
        <>
          <div className="rsvp-section-header">
            <h4 className="rsvp-section-title">
              Waitlisted Employees ({waitlisted.length})
            </h4>
          </div>
          {waitlisted.map(renderRsvp)}
        </>
      )}
    </div>
  );
};

export default RSVPList;
